import React, { useState, useEffect } from 'react';
import {
  Text,
  Box,
  HStack,
  VStack,
  Pressable,
  Icon,
  Spinner,
  Button,
  ScrollView,
} from '@gluestack-ui/themed';
import { useFormContext, useFieldArray, useWatch } from 'react-hook-form';
import {
  WandSparkles, InfoIcon, PlusCircle, Copy, Check,
} from 'lucide-react-native';
import * as Clipboard from 'expo-clipboard';
import { useTranslation } from 'react-i18next';

import { useApiErrorReporter } from '@/common/utils/apiErrorReporter';
import { useGenerateTaskProperty } from '../../../../common/hooks/lecturer/useGenerateTaskProperty';
import { useTenantGradingSettings } from '../../../../common/context/TenantSettingsStore';
import { getTaskRubricTrainingData } from '../../../../common/api/endpoints/ml-training-data/ml-training-data';
import log from '../../../../common/utils/logger';
import { useDialog } from '../../../../common/context/DialogContext';

// Import extracted components
import CriterionItem from './CriterionItem';
import DimensionHeader from './DimensionHeader';
import FlowchartSection from './FlowchartSection';

interface Criterion {
  id: string;
  name: string;
  pts: number | string;
  descriptor: string;
  dimension_critical: boolean;
}

interface Dimension {
  id: string;
  name: string;
  max_pts: number;
  criteria: Criterion[];
  params: Record<string, unknown>;
}

interface DimensionSectionProps {
  dimensionIndex: number;
  dimensionName: string;
  dimensionLabel: string;
  dimensionColor: string;
  isReadOnly: boolean;
}

/**
 * DimensionSection component handles a single dimension with its criteria
 * Uses nested useFieldArray for proper criteria management
 */
const DimensionSection: React.FC<DimensionSectionProps> = ({
  dimensionIndex,
  dimensionName,
  dimensionLabel,
  dimensionColor,
  isReadOnly,
}) => {
  const { t } = useTranslation();
  const { control, setValue, getValues } = useFormContext();

  // Nested useFieldArray for criteria within this dimension
  const {
    fields: criteriaFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: `rubric.dimensions.${dimensionIndex}.criteria`,
  });

  // Subscribe to actual pts values so the header total and stored max_pts
  // both react to user typing -- useFieldArray's `fields` snapshot only
  // updates on structural changes (append/remove), so summing from it is
  // stale while the user edits.
  const watchedCriteria = useWatch({
    control,
    name: `rubric.dimensions.${dimensionIndex}.criteria`,
    defaultValue: criteriaFields,
  }) as Array<{ pts?: number | string }> | undefined;

  const totalPoints = React.useMemo(
    () => (watchedCriteria ?? []).reduce((sum, c) => sum + (Number(c?.pts) || 0), 0),
    [watchedCriteria],
  );

  // Keep `dimensions[i].max_pts` in sync with the visible total. Without this,
  // the persisted `max_pts` drifts away from the actual criteria sum as soon
  // as the lecturer adds/removes/edits a criterion -- which is exactly the
  // kind of "saved but state still wrong" failure mode users hit.
  React.useEffect(() => {
    const currentMaxPts = Number(getValues(`rubric.dimensions.${dimensionIndex}.max_pts`)) || 0;
    if (currentMaxPts !== totalPoints) {
      setValue(`rubric.dimensions.${dimensionIndex}.max_pts`, totalPoints, {
        shouldDirty: true,
        shouldValidate: false,
      });
    }
  }, [totalPoints, dimensionIndex, setValue, getValues]);

  // Add a new criterion
  const addCriterion = () => {
    append({
      name: '',
      pts: 0,
      descriptor: '',
      dimension_critical: false,
    });
  };

  // Remove a criterion
  const removeCriterion = (criterionIndex: number) => {
    remove(criterionIndex);
  };

  return (
    <VStack space="md" mb="$4">
      {/* Dimension Header */}
      <DimensionHeader
        dimensionName={dimensionName}
        dimensionLabel={dimensionLabel}
        dimensionColor={dimensionColor}
        totalPoints={totalPoints}
      />

      {/* Criteria for this dimension */}
      <Box pr="$2" py="$1">
        {criteriaFields.length === 0 ? (
          <Text size="xs" color="$textHint" italic>
            {t('lecturer.moduleEditor.taskEditor.taskEvalForm.labels.noCriteria')}
          </Text>
        ) : null}

        {/* Grid Layout with Flex */}
        <Box display="flex" flexDirection="row" flexWrap="wrap" justifyContent="flex-start" width="100%" gap="$3">
          {criteriaFields.map((field, criterionIndex) => (
            <CriterionItem
              key={field.id}
              dimensionIndex={dimensionIndex}
              criterionIndex={criterionIndex}
              dimensionColor={dimensionColor}
              isReadOnly={isReadOnly}
              onRemove={removeCriterion}
            />
          ))}

          {/* Add Criterion Button */}
          {!isReadOnly && (
            <Pressable
              onPress={addCriterion}
              borderWidth={1}
              borderColor="$borderLight"
              borderStyle="dashed"
              borderRadius="$md"
              p="$3"
              $base-w="100%"
              $md-w="32%"
              $lg-w="32%"
              minHeight={120}
              justifyContent="center"
              alignItems="center"
              bg="$bgCard"
              $hover={{ bg: '$bgHover' }}
            >
              <Icon as={PlusCircle} size="md" color="$textHint" />
              <Text size="xs" color="$textHint" mt="$2">
                {t('lecturer.moduleEditor.taskEditor.taskEvalForm.buttons.addCriterion')}
              </Text>
            </Pressable>
          )}
        </Box>
      </Box>
    </VStack>
  );
};

interface GradingRubricFormProps {
  isReadOnly?: boolean;
  moduleId?: string;
  version?: string;
  taskId?: string | null;
}

/**
 * GradingRubricForm component for creating and editing grading rubrics
 * with dimensions and criteria
 */
const GradingRubricForm: React.FC<GradingRubricFormProps> = ({
  isReadOnly = false,
  moduleId,
  version,
  taskId: selectedTaskId,
}) => {
  const { t } = useTranslation();
  const {
    control, getValues, setValue, watch,
  } = useFormContext();
  const [showFlowchart, setShowFlowchart] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const { evalDimensions, evalDimensionKeys, dimensionColors } = useTenantGradingSettings();
  const { showToastMessage } = useDialog();
  const { reportApiError } = useApiErrorReporter();

  // Watch formal criteria source to determine if we should show formal dimension
  const formalCriteriaSource = watch('evaluation.formalCriteriaSource');

  // Get the rubric field array for dimensions
  const { fields: dimensionFields, replace: replaceDimensions } = useFieldArray({
    control,
    name: 'rubric.dimensions',
  });

  // Get dimensions that have objectives
  const objectives = getValues('analysis') || {};

  // Effect to manage formal dimension when formalCriteriaSource changes
  useEffect(() => {
    if (formalCriteriaSource === 'rubric') {
      const currentDimensions = getValues('rubric.dimensions') || [];
      const hasFormalDimension = currentDimensions.some((dim: { name: string }) => dim.name === 'formal');

      if (!hasFormalDimension) {
        const newDimensions = [
          {
            name: 'formal',
            max_pts: 0,
            criteria: [],
            params: {},
          },
          ...currentDimensions,
        ];
        replaceDimensions(newDimensions);
      }
    } else if (formalCriteriaSource !== 'rubric') {
      const currentDimensions = getValues('rubric.dimensions') || [];
      const formalDimensionIndex = currentDimensions.findIndex((dim: { name: string }) => dim.name === 'formal');

      if (formalDimensionIndex !== -1) {
        const newDimensions = currentDimensions.filter((dim: { name: string }) => dim.name !== 'formal');
        replaceDimensions(newDimensions);
      }
    }
  }, [formalCriteriaSource, getValues, replaceDimensions]);

  /**
   * Get current form data to pass to the AI
   */
  const getDraftTaskData = () => {
    const dimensions: Record<string, { applicable: boolean; demands: string[] }> = {};
    evalDimensionKeys.forEach((dim: string) => {
      if (dim !== 'formal') {
        const demands = objectives[dim] || [];
        dimensions[dim] = {
          applicable: demands.length > 0,
          demands,
        };
      }
    });

    return {
      title: getValues('title') || '',
      description: getValues('description') || '',
      analysis: { dimensions },
    };
  };

  const { startStream, isStreaming } = useGenerateTaskProperty({
    property: 'rubric',
    moduleId,
    version,
    taskId: selectedTaskId,
    onMessage: () => {
      setIsAnalyzing(false);
    },
    onServerClose: (finalMessage: {
      valid: boolean;
      data: Record<string, unknown>;
    }) => {
      if (!finalMessage?.valid) {
        showToastMessage('error', 'Error generating rubric, resetting form');
        replaceDimensions([]);
        setValue('rubric.reasoningFlowchart', '', { shouldValidate: true, shouldDirty: true });
        return;
      }
      if (finalMessage?.data) {
        const data = finalMessage.data as unknown as { dimensions: Dimension[]; reasoningFlowchart: string };
        if (data.dimensions && Array.isArray(data.dimensions)) {
          replaceDimensions(data.dimensions);
        }
        setValue('rubric.reasoningFlowchart', data.reasoningFlowchart, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    },
    onError: (error: Error) => {
      reportApiError(error);
      replaceDimensions([]);
      setValue('rubric.reasoningFlowchart', '', { shouldValidate: true, shouldDirty: true });
    },
  });

  /**
   * Handle streaming task rubric from AI
   */
  const handleStream = async () => {
    setShowFlowchart(false);
    setIsAnalyzing(true);
    replaceDimensions([]);
    setValue('rubric.reasoningFlowchart', '', { shouldValidate: true, shouldDirty: true });

    const draftTask = getDraftTaskData();
    await startStream(null, draftTask);
  };

  /**
   * Get rubric training data from backend and copy to clipboard
   */
  const copyRubricToClipboard = async () => {
    try {
      if (!moduleId || !version || !selectedTaskId) return;
      const response = await getTaskRubricTrainingData(moduleId, version, selectedTaskId);
      const trainingData = response.data.data;

      if (trainingData) {
        await Clipboard.setStringAsync(trainingData);
        setIsCopied(true);
        showToastMessage('success', t('lecturer.moduleEditor.taskEditor.messages.rubricCopied'));
        setTimeout(() => setIsCopied(false), 2000);
      }
    } catch (error) {
      log.error('Error copying rubric:', error);
      showToastMessage('error', t('lecturer.moduleEditor.taskEditor.messages.rubricCopyFailed'));
    }
  };

  const isFormReadOnly = isStreaming || isReadOnly;

  return (
    <ScrollView flex={1} bg="$bgApp">
      <VStack space="xl" px="$6" py="$4">
        {/* Header Section */}
        <HStack alignItems="center" justifyContent="space-between">
          <HStack alignItems="center" space="sm">
            <Text
              size="xs"
              color="$textSubtle"
              fontWeight="bold"
              textTransform="uppercase"
              letterSpacing="$md"
            >
              {t('lecturer.moduleEditor.taskEditor.taskEvalForm.labels.gradingRubric')}
            </Text>
            {isStreaming ? (
              <HStack space="xs" alignItems="center">
                <Spinner size="small" />
                <Text size="xs" color="$textSubtle">
                  {isAnalyzing ? 'Thinking...' : 'Creating rubric...'}
                </Text>
              </HStack>
            ) : (
              <HStack alignItems="center" space="xs">
                <Pressable onPress={handleStream} disabled={isFormReadOnly}>
                  <Icon
                    as={WandSparkles}
                    size="xs"
                    color={isFormReadOnly ? '$textDisabled' : '$brandBase'}
                  />
                </Pressable>
                {!isStreaming && (getValues('rubric.dimensions')?.length || 0) > 0 && (
                  <Pressable onPress={copyRubricToClipboard}>
                    <Icon as={isCopied ? Check : Copy} size="xs" color="$brandBase" />
                  </Pressable>
                )}
              </HStack>
            )}
          </HStack>

          {getValues('rubric.reasoningFlowchart') ? (
            <Button variant="link" onPress={() => setShowFlowchart(!showFlowchart)} size="xs">
              <HStack space="xs" alignItems="center">
                <Icon as={InfoIcon} size="xs" color="$brandBase" />
                <Text size="xs" color="$brandBase">
                  {showFlowchart
                    ? t('lecturer.moduleEditor.taskEditor.taskEvalForm.buttons.hideReasoning')
                    : t('lecturer.moduleEditor.taskEditor.taskEvalForm.buttons.showReasoning')}
                </Text>
              </HStack>
            </Button>
          ) : null}
        </HStack>

        {/* Flowchart Section */}
        {showFlowchart ? (
          <FlowchartSection flowchartCode={getValues('rubric.reasoningFlowchart')} height={200} />
        ) : null}

        {/* Dimensions Section */}
        {dimensionFields.map((dimension, dimensionIndex) => {
          const dimKey = (dimension as unknown as { name: string }).name || '';
          const dimensionName = dimKey.toUpperCase();
          const dimensionLabel = evalDimensions[dimKey] || '';
          const dimensionColor = dimensionColors ? dimensionColors[dimKey] : '$textSubtle';

          return (
            <DimensionSection
              key={dimension.id}
              dimensionIndex={dimensionIndex}
              dimensionName={dimensionName}
              dimensionLabel={dimensionLabel}
              dimensionColor={dimensionColor}
              isReadOnly={isFormReadOnly}
            />
          );
        })}
      </VStack>
    </ScrollView>
  );
};

export default GradingRubricForm;
